import React, { useEffect, useState } from 'react';
import { connect } from "react-redux";
import { useLocation, withRouter } from 'react-router-dom';
import ReactGA from 'react-ga';
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import AccountsHeader from './ui-componets/accounts-header';
import AccountsFooter from './ui-componets/accounts-footer';
import { getBalance, getUserAndCountry } from "./modules/ui.ducks";
import { logOut, getSelfServiceInfo, getCode } from './modules/join.ducks';

import 'semantic-ui-css/semantic.min.css';
import './styles/tailwind.css';
import './styles/index.css';
import './styles/custom.css';
import './styles/mobile.css';
import './styles/ipad.css';
import "./styles/rtl.css";
import Routes from "./Routes";

// @ts-ignore
import { RootState } from "./redux/store";
import Loader from './ui-componets/loader';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';

const App: React.FC = (props: any) => {
    const { t } = useTranslation();
    props = {...props , shopUser: props?.join?.loginDetails?.shopOnly , dieselCardUser: props?.join?.loginDetails?.dieselCardUser }
    const location = useLocation();
    let countryCode = props?.join?.loginDetails?.userCountryCode || props?.join?.loginDetails?.countryCode || 'GB';
    const { getBalance, logOut, global, selfServiceInfo, getSelfServiceInfo, getUserAndCountry } = props;
    
    const [fetchingCode, updateFetchingCode] = useState(false);

    const translateChatButton = () => {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            const chatButtonText = document.querySelector('.circleRollButtonText');
            if (chatButtonText) {
              const possibleTexts = [
                t('Live Chat'),
                t('Contact Us'),
                t('Live chat button'),
                t('Contact us button')
              ];
      
              // Translate the text content
              chatButtonText.textContent = t(`${chatButtonText.textContent}`);

              // Update the title attribute
              const titleDiv = chatButtonText.closest('div[title]');
              if (titleDiv) {
                titleDiv.setAttribute('title', t(`${titleDiv.getAttribute('title')}`));
              }
      
              // Stop observing once the element is translated
              observer.disconnect();
            }
          });
        });
      
        // Start observing the body or a specific parent node
        observer.observe(document.body, { childList: true, subtree: true });
    };

    useEffect(() => {
        // Only call APIs if token exists and is valid
        if (props.join.tokens && props.join.tokens.access_token) {
            getSelfServiceInfo();
            getBalance();
        }
    }, [props.join.tokens]);

    useEffect(() => {
        // Only call getUserAndCountry if token exists and is valid
        if (props.join.tokens && props.join.tokens.access_token) {
            getUserAndCountry();
        }
    }, [location.pathname, props.join.tokens]); // Reactively trigger effect on route change or token update


    // listen to window message of LiveAgent chat so that the language of the user can be sent to initiate translation
    useEffect(() => {
        const handleMessage = (event: any) => {
          // Check if the message is from the expected origin and the data matches
          if (event.origin === 'https://iat-ltd.ladesk.com' && event.data === 'EP_LA_CHAT_LOADED') {
            const languageCode = props.join?.loginDetails?.userLanguageCode; // Replace with the dynamic language code - Default to 'en_GB' if not set
            const rtlFlag = props.join?.loginDetails?.rtl; // Replace with the dynamic value of rtl for the language
            event.source?.postMessage(
              { type: 'SET_LANGUAGE_CODE', language: languageCode, rtlFlag },
              '*'
            );
          }
        };
    
        // Attach the event listener
        window.addEventListener('message', handleMessage);
    
        // Cleanup the event listener on component unmount
        return () => {
          window.removeEventListener('message', handleMessage);
        };
    }, [props.join?.loginDetails?.userLanguageCode]);

    useEffect(() => {
        // Set the language to selected language or any other language dynamically
        i18n.changeLanguage(props.join?.loginDetails?.userLanguageCode).then(() => {
          translateChatButton();
        });
    }, [props.join?.loginDetails?.userLanguageCode]);
    
    useEffect(() => {
        if(selfServiceInfo && selfServiceInfo.isSelfServiceUser && !selfServiceInfo.isPaymentCompleted){
            props.history.push('/NoAccess');
        }
    }, [selfServiceInfo]);

    useEffect(() => {
        // Anything in here is fired on component mount.
        const unlisten = props.history.listen((location: any, action: any) => {
            if (window.location.hostname == 'www.eachperson.com')
                ReactGA.initialize('UA-41781197-8');
            else
                ReactGA.initialize('UA-41781197-1');
            ReactGA.pageview(window.location.pathname + window.location.search);
        });
        return () => {
            // Anything in here is fired on component unmount.
            unlisten();
        }
    }, [])

    const onURLChange = (url: string) => {
        props.history.push(url);
    }

    // get sso object from localStorage
    let ssoData = global.SSO_USER;
    let isSsoUser = ssoData?.enabled;
    let companyShortName = ssoData?.companyShortName;

    const redirecrToShop = async () => {        
        if(props?.join?.loginDetails?.onlyDashboard || (props?.join?.loginDetails?.userTypeFeatureAccessTO && !props?.join?.loginDetails?.userTypeFeatureAccessTO?.hasShopAccess)) {
            window.location.href = ''
        }
        
        if(isSsoUser) {
            window.location.href = `${global.EP_ADMIN_URL}/login-handler?redirect_to=new-shop&companyShortName=${companyShortName}`;
        } else {
            updateFetchingCode(true)
            try {
                let userCode = await getCode() as { authenticationCode?: string }; // Adjust the type based on the actual structure;
          
                if (userCode && typeof userCode === 'object') {
                    if ('authenticationCode' in userCode) {
                        window.location.href = `${global.SHOP_URL}/?code=${userCode.authenticationCode}`;
                    } else {
                        updateFetchingCode(false)
                        window.location.href = '';
                    }
                } else {
                    updateFetchingCode(false)
                    window.location.href = '';
                }
            } catch (error) {
                updateFetchingCode(false);
                window.location.href = '';
            }
        }
    }

    const redirectingDomainSpecificPrivacy = async () => {
        if(isSsoUser) {
            window.location.href = `${global.EP_ADMIN_URL}/login-handler?redirect_to=new-shop&companyShortName=${companyShortName}&intended_url=${global.SHOP_URL}/privacy-policy/`;
        } else {
            updateFetchingCode(true)
            try {
                let userCode = await getCode() as { authenticationCode?: string }; // Adjust the type based on the actual structure;
          
                if (userCode && typeof userCode === 'object') {
                    if ('authenticationCode' in userCode) {
                        window.location.href = `${global.SHOP_URL}/?code=${userCode.authenticationCode}&redirect_to=${global.SHOP_URL}/privacy-policy/`;
                    } else {
                        updateFetchingCode(false)
                        window.location.href = '';
                    }
                } else {
                    updateFetchingCode(false)
                    window.location.href = '';
                }
            } catch (error) {
                updateFetchingCode(false);
                window.location.href = '';
            }
        }
    }

    const redirectingDomainSpecificCookies = () => {
        let RedirectDomainName = 'https://www.eachperson.com/cookie-policy/';
        return RedirectDomainName;
    }

    const redirectingDomainSpecificFaq = async() => {
        if(isSsoUser) {
            window.location.href = `${global.EP_ADMIN_URL}/login-handler?redirect_to=new-shop&companyShortName=${companyShortName}&intended_url=${global.SHOP_URL}/faqs/`;
        } else {
            updateFetchingCode(true)
            try {
                let userCode = await getCode() as { authenticationCode?: string }; // Adjust the type based on the actual structure;
          
                if (userCode && typeof userCode === 'object') {
                    if ('authenticationCode' in userCode) {
                        window.location.href = `${global.SHOP_URL}/?code=${userCode.authenticationCode}&redirect_to=${global.SHOP_URL}/faqs/`;
                    } else {
                        updateFetchingCode(false)
                        window.location.href = '';
                    }
                } else {
                    updateFetchingCode(false)
                    window.location.href = '';
                }
            } catch (error) {
                updateFetchingCode(false)
                window.location.href = '';
            }
        }
    }

    const redirecrToEcahPerson = async() => {
        if (isSsoUser) {
            window.location.href = `${global.EP_ADMIN_URL}/sso?companyShortName=${companyShortName}&redirectUrl=hr/dashboard`;
        } else {
            updateFetchingCode(true)
            try {
                let userCode = await getCode() as { authenticationCode?: string }; // Adjust the type based on the actual structure;
          
                if (userCode && typeof userCode === 'object') {
                    if ('authenticationCode' in userCode) {
                        window.location.href = `${global.EP_ADMIN_URL}/seamless-redirection?code=${userCode.authenticationCode}`;
                    } else {
                        updateFetchingCode(false)
                        window.location.href = '';
                    }
                } else {
                    updateFetchingCode(false)
                    window.location.href = '';
                }
            } catch (error) {
                updateFetchingCode(false)
                window.location.href = '';
            }
        }
    }
    const redirecrToAbout = () => {
        let RedirectDomainName = 'https://www.eachperson.com/about/';
        return RedirectDomainName;
    }
    const redirecrToContact = async() => {
        if(isSsoUser) {
            window.location.href = `${global.EP_ADMIN_URL}/sso?companyShortName=${companyShortName}&redirectUrl=contact-us`;
        } else {
            updateFetchingCode(true)
            try {
                let userCode = await getCode() as { authenticationCode?: string }; // Adjust the type based on the actual structure;
        
                if (userCode && typeof userCode === 'object') {
                    if ('authenticationCode' in userCode) {
                        if (props?.shopUser){
                            window.location.href = `${props.global.SHOP_URL}/?code=${userCode?.authenticationCode}&redirect_to=${props.global.SHOP_URL}/contact-us/`;
                        } else {
                            window.location.href = `${props.global.EP_ADMIN_URL}/seamless-redirection?code=${userCode?.authenticationCode}&state=/contact-us`;
                        }
                    } else {
                        updateFetchingCode(false)
                        window.location.href = '';
                    }
                } else {
                    updateFetchingCode(false)
                    window.location.href = '';
                }
            } catch (error) {
                updateFetchingCode(false)
                window.location.href = '';
            }
        }
        
    }

    const redirecrToDonate = async() => {
        // Allow access if userTypeFeatureAccess is undefined, otherwise check hasDonationAccess
        if(props?.join?.loginDetails?.userTypeFeatureAccessTO && !props?.join?.loginDetails?.userTypeFeatureAccessTO?.hasDonationAccess) {
            window.location.href = '';
            return;
        }

        if(isSsoUser) {
            window.location.href = `${global.EP_ADMIN_URL}/sso?companyShortName=${companyShortName}&redirectUrl=hr/donate`;
        } else {
            updateFetchingCode(true)
            try {
                let userCode = await getCode() as { authenticationCode?: string }; // Adjust the type based on the actual structure;
        
                if (userCode && typeof userCode === 'object') {
                    if ('authenticationCode' in userCode) {
                        window.location.href = `${props.global.EP_ADMIN_URL}/seamless-redirection?code=${userCode?.authenticationCode}&state=/hr/donate`;
                    } else {
                        updateFetchingCode(false)
                        window.location.href = '';
                    }
                } else {
                    updateFetchingCode(false)
                    window.location.href = '';
                }
            } catch (error) {
                updateFetchingCode(false)
                window.location.href = '';
            }
        }
    }

    const gotoLink = (tag: string) => {
        switch (tag) {
            case 'your-account':
                onURLChange('/account')
                break;
            case 'your-details':
                onURLChange('/details')
                break;
            case 'your-activity':
                onURLChange('/activity')
                break;
            case 'your-orders':
                onURLChange('/orders')
                break;
            case 'your-address':
                onURLChange('/addresses')
                break;
            case 'marketing-preferences':
                onURLChange('/preferences')
                break;
            case 'membership-details':
                onURLChange('/membership')
                break;
            case 'payment-details':
                onURLChange('/payments')
                break;
            case 'donate':
                redirecrToDonate();
                break;
            case 'work-profile':
                onURLChange('/profile')
                break;
            case 'epoints':
                redirecrToShop();
                break;
            case 'each-person':
                redirecrToEcahPerson();
                break;
            case 'logout':
                // clearTokens();
                logOut();
                break;
            default:
                props.history.push(`/${tag}`);
                break;
        }
    }
    const gotoFooterLink = (tag: string) => {
        switch (tag) {
            case 'privacy':
                redirectingDomainSpecificPrivacy();
                break;
            case 'cookie-policy':
                window.location.href = redirectingDomainSpecificCookies();
                break;
            case 'faq':
                redirectingDomainSpecificFaq();
                break;
            case 'about':
                window.location.href = redirecrToAbout();
                break;
            case 'epoints':
                redirecrToShop();
                break;
            case 'contact-us':
                redirecrToContact();
                break;
            default:
                props.history.push(`/${tag}`);
                break;
        }
    }
    const renderSimpleHeader = () => location.pathname.split('/')[1] === 'join'

    return (
        <>
            {fetchingCode ?
                <div><Loader/></div> :
            <>
                <main className="accounts-container" onClick={(e) => (e)}>
                    <div className="accounts-body-binder">

                        <section>
                            <AccountsHeader
                                simpleHeaderAccounts={renderSimpleHeader()}
                                handleLinkClick={(tag: string) => gotoLink(tag)} 
                                isGbUser={props.join.loginDetails && countryCode && countryCode === 'GB' ? true : false}
                                showBlock={(props.join && props.join.loginDetails && props.join.loginDetails.isBusinessUser == true) ||  (props.join.tokens && !props.join.tokens.access_token)  ? true : false }
                                token={props.join.tokens?.access_token}
                                refreshToken={props.join.tokens?.refresh_token}
                                shopUser={props.shopUser}
                                onlyDashboardUser={props?.join?.loginDetails?.onlyDashboard}
                                userTypeFeatureAccess={props?.join?.loginDetails?.userTypeFeatureAccessTO}
                                />
                            <Routes {...props} />
                        </section>
                    </div>
                </main>
                {/* <div className="helplines">
                    <HelplineStrip />
                </div> */}
                <div><AccountsFooter handleLinkClick={(tag: string) => gotoFooterLink(tag)}></AccountsFooter></div>
            </>
        }
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    global: state.global,
    join: state.join,
    selfServiceInfo : state.join.selfServiceInfo
});

export default withRouter(connect(mapStateToProps, { logOut, getBalance, getUserAndCountry, getSelfServiceInfo })(App));
